import { Injectable } from '@angular/core'
import { environment } from '@se-po/shared-environments'
import { Observable, of } from 'rxjs'
import { catchError, map, take } from 'rxjs/operators'
import { ApiService, Store } from '.'
import { FinancialSummary, Sale, SaleActivity } from '../models'

const OUTSTANDING_INVOICE_STATUSES = ['unpaid', 'partial-paid', 'past-due']
const OUTSTANDING_PAYMENT_STATUSES = ['unpaid', 'past-due']
type InvoicePayment = Sale['payments'][number]

@Injectable({
  providedIn: 'root'
})
export class InvoiceService extends Store<Sale[]>{
  constructor(private api: ApiService) {
    super(null)
  }

  public find(params: any, options?: any): void {
    this.mine(params, options).pipe(take(1)).subscribe(
      (data: Sale[]) => {
        this.setCurrent(data)
      }
    )
  }

  public get(saleNumber: string): Observable<Sale>{
    return this.api.findAs(
      `invoices/${saleNumber}.json`,
      Sale)
  }

  public significantActivity(id: number): Observable<SaleActivity[]> {
    return this.api.findAllAs(
      `invoices/${id}/activity`,
      { params: { view_mode: 'user', significance: 'high' }},
      SaleActivity)
  }

  public financialSummary(options?: any): Observable<FinancialSummary[]> {
    return this.mine(
      { 'monetary_status[]': OUTSTANDING_INVOICE_STATUSES },
      options
    ).pipe(
      map(invoices => this.toFinancialSummaries(invoices)),
      catchError(() => of([]))
    )
  }

  private mine(params: any, options?: any): Observable<Sale[]> {
    const defaults = { page: 1, per_page: 25 }
    return this.api.findAllAs(
      'invoices/mine',
      {
        ...options,
        params: {...defaults, ...params}
      },
      Sale)
  }

  private toFinancialSummaries(invoices: Sale[]): FinancialSummary[] {
    return invoices
      .flatMap(invoice =>
        invoice.payments
          .filter(payment => this.isOutstanding(payment))
          .map(payment => this.toFinancialSummary(invoice, payment))
      )
      .sort((summaryA, summaryB) =>
        this.compareFinancialSummaries(summaryA, summaryB)
      )
  }

  private isOutstanding(payment: InvoicePayment): boolean {
    return OUTSTANDING_PAYMENT_STATUSES.includes(payment.status)
  }

  private toFinancialSummary(
    invoice: Sale,
    payment: InvoicePayment
  ): FinancialSummary {
    const state = ['Unpaid']
    if (payment.due_date_days_past > 0) state.push('PastDue')

    const paymentMethod = invoice.payment_plan_payment_method
    let paymentDescription: string
    if (paymentMethod?.acct_type && paymentMethod?.acct_num) {
      state.push('AutoPay')
      paymentDescription = `${paymentMethod.acct_type} ending in ${paymentMethod.acct_num}`
    }

    return new FinancialSummary({
      id: payment.id,
      amount: Math.round(Number(payment.amount) * 100),
      description: invoice.description,
      dueDate: payment.due_date,
      href: `${environment.sePayments}${invoice.sale_number}/cart`,
      sourceType: invoice.type,
      state,
      paymentDescription
    })
  }

  private compareFinancialSummaries(
    summaryA: FinancialSummary,
    summaryB: FinancialSummary
  ): number {
    const pastDueOrder =
      Number(summaryB.state.includes('PastDue')) -
      Number(summaryA.state.includes('PastDue'))
    return pastDueOrder || summaryA.dueDate.localeCompare(summaryB.dueDate)
  }
}
